import { isEmpty } from "lodash";
import { operationDefinition, accessPolicyTypeDefinition } from "../../../../const";

export const getPortMediaType = ( ({
  selectedRows
}) => {
  let result = {
    isCopper: false,
    isFiber: false,
    copperPorts: [],
    fibberPorts: [],
  }

  if(!selectedRows) return result;

  const selectedPorts = selectedRows.map(row => row.original);

  selectedPorts.forEach(port => {
    const mediaType = +(port?.switchPort?.portStaMediaType);

    if(mediaType === 1) {
      result.isCopper = true;
      result.copperPorts.push(port);
    }else if(mediaType === 2){
      result.isFiber = true;
      result.fibberPorts.push(port);
    }
  });

  return result;
});

export const getPortTagsRequestParams = ({ auth, method = "read" }) => {
  const isStandaloneMode = !auth.orgs || Object.keys(auth.orgs).length === 0;
  const apiBaseUrl = isStandaloneMode ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;
  const apiPaths = `${apiBaseUrl}/sites/:siteId/settings/tags/portTags.${method}`;

  const restfulParamsConfig = {
    standalone: {
      orgId: auth.realm?.id,
      siteId: auth.sites?.id,
    },
    nonStandalone: {
      entId: auth.realm?.id,
      orgId: auth.orgs?.id,
      siteId: auth.sites?.id,
    },
  };

  const restfulParams = isStandaloneMode ? restfulParamsConfig["standalone"] : restfulParamsConfig["nonStandalone"];

  return {
    apiPaths,
    restfulParams
  };
}

export const getPortTagsCreatePayload = ({ tags }) => {
  return {
    data: {
      tagName: tags?.tagName ? tags.tagName : null
    }
  }
}

export const initChips = ({ chips, tags }) => {
  // 1. Use chip id to change the format to { tagName: xxx, tagId: xxx }.
  // 2. Remove saved chips which no longer exist in tags.
  const comparisonEnum = tags.reduce((acc, tag) => {
    return {
      ...acc,
      [tag.tagId]: tag
    }
  }, {});

  const newChips = chips.map(chipId => {
    const matchedChip = comparisonEnum[chipId];
    return isEmpty(matchedChip) ? null : matchedChip;
  }).filter(chip => !!chip)

  return newChips
}

// 這是在針對前端暫存階段 ( 尚未 Apply 儲存時 ), 要把關聯的 mirror / aggregation ports 全部同步.
// 避免 Apply 時, 會因為 user 可能的操作個別的 port 而造成 關聯 of ports 的資料不一樣.
export const getUpdatedAssociatedPorts = ({
  switchesData,
  activeRow,
  originalRowData,
  updatedPort,
  excludeIndices = []
}) => {
  const activeIdx = +activeRow.portIndex;

  const { type: oldType = "switching", mirrorPort: oldMirrorPorts = [], aggregatePort: oldAggPorts = [] } = originalRowData.operation || {};
  const { type: newType = "switching", mirrorPort: newMirrorPorts = [], aggregatePort: newAggPorts = [] } = updatedPort.operation || {};

  const newMirrorGroup = [activeIdx, ...newMirrorPorts]; // [ self, ...associated mirror ports ]
  const newAggregateGroup = [activeIdx, ...newAggPorts]; // [ self, ...associated aggregate ports ]

  return switchesData.map(loopSwitch => {
    // 僅針對同台 switch device 的 port 進行同步.
    if (loopSwitch.macAddress !== activeRow.macAddress) return loopSwitch;

    const portIdx = +loopSwitch.portIndex;

    // User 正在直接編輯的 selectedPort.
    if (portIdx === activeIdx) {
      return {
        ...updatedPort,
        isWaitingEdit: true
      };
    }

    // 如果該 port 是本次批次編輯的其中一個 port，則跳過同步，避免被舊狀態覆寫.
    if (excludeIndices.includes(portIdx)) {
      return loopSwitch;
    }

    let op = { ...loopSwitch.operation };
    let changed = false;

    // 1. 切到 mirror mode 時.
    if (newType === "mirroring") {
      if (newMirrorPorts.includes(portIdx)) {
        // 被 mirror 的 source ports 設為 switching.
        op.type = "switching";
        op.mirrorPort = [];
        op.mirrorMode = "";
        op.aggregatePort = [];
        op.aggregateGroupId = null;
        op.aggregateType = "switching";
        changed = true;
      } else if (loopSwitch.operation?.type === "mirroring" && portIdx !== activeIdx) {
        // 清除該 device 其他已存在的 mirror port (保持一個 device 只有一組 mirror group).
        op.type = "switching";
        op.mirrorPort = [];
        op.mirrorMode = "";
        op.aggregatePort = [];
        op.aggregateGroupId = null;
        op.aggregateType = "switching";
        changed = true;
      }
    }

    // 2. 切到 aggregation mode 時.
    else if (newType === "aggregation") {
      if (newAggregateGroup.includes(portIdx)) {
        // 剛被加入到 aggregation group 或同組成員.
        op.type = "aggregation";
        op.aggregatePort = newAggregateGroup.filter(id => id !== portIdx); // 排除自己.
        op.aggregateGroupId = updatedPort.operation.aggregateGroupId;
        op.aggregateType = updatedPort.operation.aggregateType;
        op.mirrorPort = [];
        op.mirrorMode = "";

        // 只有 aggregation mode 時才同步所有 port 設定
        return {
          ...loopSwitch,
          portProfile: updatedPort.portProfile,
          portState: updatedPort.portState,
          portType: updatedPort.portType,
          PVID: updatedPort.PVID,
          allowVlan: updatedPort.allowVlan,
          speed: updatedPort.speed,
          comboSfpSpeed: updatedPort.comboSfpSpeed,
          poeState: updatedPort.poeState,
          ddpState: updatedPort.ddpState,
          lbdState: updatedPort.lbdState,
          rstpState: updatedPort.rstpState,
          stpState: updatedPort.stpState,
          isPortScheduleMode: updatedPort.isPortScheduleMode,
          isPoeScheduleMode: updatedPort.isPoeScheduleMode,
          portSchedule: updatedPort.portSchedule,
          poeSchedule: updatedPort.poeSchedule,
          accessPolicy: updatedPort.accessPolicy,
          ...(updatedPort.portTags !== undefined && { portTags: updatedPort.portTags }),
          ...(updatedPort.tags !== undefined && { tags: updatedPort.tags }),
          ...(updatedPort.combinePortName !== undefined && { combinePortName: updatedPort.combinePortName }),
          operation: op,
          isWaitingEdit: true
        };
      } else if (oldType === "aggregation" && oldAggPorts.includes(portIdx)) {
        // 切成 switching mode, 被移出 aggregation group.
        op.type = "switching";
        op.aggregatePort = [];
        op.aggregateGroupId = null;
        op.aggregateType = "switching";
        changed = true;
      }

      // 如果該 port 原本是 mirror port，且它的 group 成員（或是它自己）被拉去 aggregation，則清除或更新 mirror group.
      if (loopSwitch.operation?.type === "mirroring" && portIdx !== activeIdx) {
        if (newAggregateGroup.includes(portIdx)) {
          // mirror port 本身被拉去 aggregation ( mirror group 解散 ).
          op.type = "switching";
          op.mirrorPort = [];
          op.mirrorMode = "";
          changed = true;
        } else {
          // 檢查是否有被 mirror 的 source ports 被拉去 aggregation.
          const currentMirror = loopSwitch.operation.mirrorPort || [];
          const updatedMirror = currentMirror.filter(id => !newAggregateGroup.includes(id));
          if (updatedMirror.length !== currentMirror.length) {
            if (updatedMirror.length === 0) {
              op.type = "switching";
              op.mirrorPort = [];
              op.mirrorMode = "";
            } else {
              op.mirrorPort = updatedMirror;
            }
            changed = true;
          }
        }
      }
    }

    // 3. 切到 switching mode 時.
    else {
      // 從 aggregation group 中移除 port.
      if (oldType === "aggregation" && oldAggPorts.includes(portIdx)) {
        const hasRemainingGroup = oldAggPorts.length >= 2; // 當 group 中至少還有2個 ports, 是合法的.
        op.type = hasRemainingGroup ? "aggregation" : "switching"; // 合法的就保持原 mode.
        op.aggregatePort = hasRemainingGroup ? oldAggPorts.filter(id => id !== portIdx) : [];
        op.aggregateGroupId = hasRemainingGroup ? op.aggregateGroupId : null;
        op.aggregateType = hasRemainingGroup ? op.aggregateType : "switching";
        changed = true;
      }
    }

    if (changed) {
      return {
        ...loopSwitch,
        operation: op,
        isWaitingEdit: true
      };
    }

    return loopSwitch;
  });
};

export const checkPortInOtherAggregateGroup = ({
  portItem,
  macAddress,
  selectedIndexList,
  switchesData
}) => {
  if (selectedIndexList.includes(portItem.portIndex)) return false;

  const devicePorts = switchesData.filter(port => port.macAddress === macAddress);
  return devicePorts.some(port => {
    if (selectedIndexList.includes(port.portIndex)) return false;
    if (port.operation?.type !== "aggregation") return false;

    // 若該 aggregationPorts 中包含任何當前編輯的 port，則代表屬同一個 group，而不是其他 group.
    const aggregationPorts = port.operation?.aggregatePort || [];
    const isSameGroup = aggregationPorts.some(port => selectedIndexList.includes(port)) || selectedIndexList.includes(Number(port.portIndex));
    if (isSameGroup) {
      return false;
    }

    return port.portIndex === portItem.portIndex || aggregationPorts.includes(portItem.portIndex);
  });
};